/**
 * CalendarGridView - Week-based calendar grid for task planning
 * Stitch design system
 */
import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { formatDateISO } from '../../utils/weekUtils';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const ROLE_COLORS = {
  'Projectleider': '#10b981', 'Accountmanager': '#3b82f6', 'Regisseur': '#7c3aed',
  'Camera Operator': '#06b6d4', 'Producer': '#f59e0b', 'Editor': '#3b82f6',
  'Animator': '#ec4899', 'Copywriter': '#8b5cf6', 'Productie-assistent': '#f97316', 'Management': '#6b7280',
};

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function formatShortDate(date) {
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function CalendarGridView({
  tasks = [], startDate, endDate, onTaskClick, onTaskDateChange,
  showExcluded = true, weeksToShow = 6, weekOffset = 0, onWeekOffsetChange,
}) {
  const start = useMemo(() => startDate ? new Date(startDate) : new Date(), [startDate]);
  const end = useMemo(() => endDate ? new Date(endDate) : new Date(start.getTime() + 42 * 24 * 60 * 60 * 1000), [endDate, start]);

  const weeks = useMemo(() => {
    const result = [];
    const baseMonday = getMonday(start);
    baseMonday.setDate(baseMonday.getDate() + (weekOffset * 7));
    for (let i = 0; i < weeksToShow; i++) {
      const weekStart = new Date(baseMonday);
      weekStart.setDate(weekStart.getDate() + (i * 7));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      result.push({ weekNum: getWeekNumber(weekStart), start: weekStart, end: weekEnd, label: `Week ${getWeekNumber(weekStart)}`, dateRange: `${formatShortDate(weekStart)} - ${formatShortDate(weekEnd)}` });
    }
    return result;
  }, [start, weekOffset, weeksToShow]);

  const tasksByWeek = useMemo(() => {
    const grouped = {};
    weeks.forEach(week => { grouped[week.weekNum] = []; });
    tasks.forEach(task => {
      if (!showExcluded && task.is_included === false) return;
      const taskStart = task.start_date ? new Date(task.start_date) : null;
      if (!taskStart) return;
      for (const week of weeks) {
        if (taskStart >= week.start && taskStart <= week.end) { if (!grouped[week.weekNum]) grouped[week.weekNum] = []; grouped[week.weekNum].push(task); break; }
      }
    });
    return grouped;
  }, [tasks, weeks, showExcluded]);

  const includedCount = tasks.filter(t => t.is_included !== false).length;
  const totalCount = tasks.length;

  const handleDragStart = (e, task) => { e.dataTransfer.setData('taskId', task.id?.toString() || ''); e.dataTransfer.effectAllowed = 'move'; };
  const handleDrop = (e, week) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId && onTaskDateChange) {
      // Lokale dag: week.start is maandag 00:00 lokaal, toISOString gaf zondag (UTC).
      const newStartDate = formatDateISO(week.start);
      const friday = new Date(week.start); friday.setDate(friday.getDate() + 4);
      onTaskDateChange(parseInt(taskId), newStartDate, formatDateISO(friday));
    }
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            onClick={() => onWeekOffsetChange?.(weekOffset - 1)}
            sx={{
              width: 28, height: 28, borderRadius: '0.35rem', cursor: weekOffset <= -4 ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: weekOffset <= -4 ? 0.3 : 1,
              '&:hover': weekOffset > -4 ? { bgcolor: T.surfaceLow } : {},
            }}
          >
            <Icon name="chevron_left" size={18} sx={{ color: T.outline }} />
          </Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Planning kalender
          </Typography>
          <Box
            onClick={() => onWeekOffsetChange?.(weekOffset + 1)}
            sx={{
              width: 28, height: 28, borderRadius: '0.35rem', cursor: weekOffset >= 12 ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: weekOffset >= 12 ? 0.3 : 1,
              '&:hover': weekOffset < 12 ? { bgcolor: T.surfaceLow } : {},
            }}
          >
            <Icon name="chevron_right" size={18} sx={{ color: T.outline }} />
          </Box>
        </Box>
        <Typography component="span" sx={{
          fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
          color: T.primary, bgcolor: T.primaryContainer,
          px: 1.25, py: 0.3, borderRadius: '9999px',
        }}>
          {includedCount}/{totalCount} taken
        </Typography>
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${weeksToShow}, 1fr)`, gap: 1, minHeight: 350 }}>
        {weeks.map((week) => {
          const weekTasks = tasksByWeek[week.weekNum] || [];
          const isCurrentWeek = getWeekNumber(new Date()) === week.weekNum;

          return (
            <Box
              key={week.weekNum}
              onDrop={(e) => handleDrop(e, week)}
              onDragOver={handleDragOver}
              sx={{
                borderRadius: '0.75rem', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                boxShadow: T.shadow,
                border: isCurrentWeek ? `2px solid ${T.primary}` : '1px solid transparent',
              }}
            >
              {/* Week header */}
              <Box sx={{
                bgcolor: isCurrentWeek ? T.primary : T.surfaceLow,
                color: isCurrentWeek ? 'white' : T.text,
                px: 1.5, py: 0.75,
              }}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700, display: 'block' }}>
                  {week.label}
                </Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', opacity: 0.7 }}>
                  {week.dateRange}
                </Typography>
              </Box>

              {/* Tasks */}
              <Box sx={{ flex: 1, p: 0.75, overflowY: 'auto', bgcolor: T.surfaceLow, display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 200 }}>
                {weekTasks.length === 0 ? (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontStyle: 'italic', color: T.outlineVariant, textAlign: 'center', py: 2 }}>
                    Sleep taken hierheen
                  </Typography>
                ) : (
                  weekTasks.map((task, idx) => {
                    const isIncluded = task.is_included !== false;
                    return (
                      <Tooltip
                        key={task.id || idx}
                        title={
                          <Box>
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem' }}>{task.name}</Typography>
                            {task.role && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', opacity: 0.8 }}>{task.role} • {task.estimated_hours || 0}u</Typography>}
                            {task.start_date && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem' }}>{task.start_date} - {task.end_date}</Typography>}
                          </Box>
                        }
                        placement="right" arrow
                        slotProps={{
                          tooltip: { sx: { fontFamily: T.fontBody, bgcolor: T.text, borderRadius: '0.5rem', px: 1.5, py: 0.75 } },
                          arrow: { sx: { color: T.text, '&::before': { border: 'none' } } },
                        }}
                      >
                        <Box
                          draggable={!!onTaskDateChange}
                          onDragStart={(e) => handleDragStart(e, task)}
                          onClick={() => onTaskClick?.(task)}
                          sx={{
                            px: 0.75, py: 0.6,
                            cursor: onTaskDateChange ? 'grab' : 'pointer',
                            borderRadius: '0.35rem',
                            borderLeft: `3px solid ${ROLE_COLORS[task.role] || T.outlineVariant}`,
                            bgcolor: isIncluded ? T.surface : T.surfaceLow,
                            opacity: isIncluded ? 1 : 0.5,
                            transition: 'all 0.1s',
                            '&:hover': { boxShadow: '0 2px 8px rgba(45,51,53,0.06)', transform: 'translateY(-1px)' },
                            '&:active': { cursor: 'grabbing' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Icon
                              name={isIncluded ? 'check_circle' : 'remove_circle'}
                              size={12}
                              sx={{ color: isIncluded ? T.emerald : T.outlineVariant, flexShrink: 0, marginTop: 1 }}
                            />
                            <Typography sx={{
                              fontFamily: T.fontBody, fontWeight: 500, fontSize: '0.64rem', lineHeight: 1.3, color: T.text,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}>
                              {task.name}
                            </Typography>
                          </Box>
                          {task.estimated_hours > 0 && (
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.52rem', color: T.outline, pl: 2 }}>
                              {task.estimated_hours}u
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default CalendarGridView;
